(function () {
  const countryCallingCodes = {
    fr: '+33 (France)',
    us: '+1 (United States)',
    ca: '+1 (Canada)',
    gb: '+44 (United Kingdom)',
    il: '+972 (Israel)',
    be: '+32 (Belgium)',
    ch: '+41 (Switzerland)',
    lu: '+352 (Luxembourg)',
    mc: '+377 (Monaco)',
    it: '+39 (Italy)',
    es: '+34 (Spain)',
    pt: '+351 (Portugal)',
    de: '+49 (Germany)',
    nl: '+31 (Netherlands)',
    at: '+43 (Austria)',
    dk: '+45 (Denmark)',
    se: '+46 (Sweden)',
    no: '+47 (Norway)',
    ie: '+353 (Ireland)',
    gr: '+30 (Greece)',
    ae: '+971 (United Arab Emirates)',
    sa: '+966 (Saudi Arabia)',
    qa: '+974 (Qatar)',
    kw: '+965 (Kuwait)',
    hk: '+852 (Hong Kong)',
    sg: '+65 (Singapore)',
    cn: '+86 (China)',
    jp: '+81 (Japan)',
    kr: '+82 (South Korea)',
    au: '+61 (Australia)',
    ru: '+7 (Russia)',
    tr: '+90 (Turkey)',
    in: '+91 (India)',
    br: '+55 (Brazil)',
    mx: '+52 (Mexico)',
    za: '+27 (South Africa)',
  };

  function getCountryName(countryData) {
    return countryData?.name ? countryData.name.split(' (')[0].trim() : '';
  }

  function getCountryCallingCode(phoneInput) {
    if (!phoneInput) return '';

    const countryData = phoneInput.getSelectedCountryData();
    if (!countryData?.dialCode) return '';
    if (countryData.iso2 && countryCallingCodes[countryData.iso2]) {
      return countryCallingCodes[countryData.iso2];
    }

    return `+${countryData.dialCode} (${getCountryName(countryData)})`;
  }

  function setStatus(statusElement, message, type) {
    statusElement.textContent = message;
    statusElement.className = `yb-proposal-form__status${type ? ` is-${type}` : ''}`;
  }

  function getRecaptchaToken(section) {
    const siteKey = section.dataset.recaptchaSiteKey;
    const action = section.dataset.recaptchaAction || 'collection_proposal';
    const isRequired = section.dataset.recaptchaRequired === 'true';

    if (!siteKey) {
      return isRequired
        ? Promise.reject(new Error('reCAPTCHA site key is missing'))
        : Promise.resolve({ action, token: '' });
    }
    if (!window.grecaptcha?.ready || !window.grecaptcha?.execute) {
      return Promise.reject(new Error('reCAPTCHA is unavailable'));
    }

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error('reCAPTCHA timed out')), 10000);

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(siteKey, { action })
          .then((token) => {
            window.clearTimeout(timeout);
            resolve({ action, token });
          })
          .catch((error) => {
            window.clearTimeout(timeout);
            reject(error);
          });
      });
    });
  }

  function initializeForm(form, section) {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';

    const phoneField = form.querySelector('[name="phone"]');
    const emailField = form.querySelector('[name="email"]');
    const statusElement = form.querySelector('.yb-proposal-form__status');
    const submitButton = form.querySelector('.yb-proposal-form__submit');
    const phoneInput = window.intlTelInput
      ? window.intlTelInput(phoneField, {
          initialCountry: 'auto',
          separateDialCode: true,
          geoIpLookup(callback) {
            try {
              const cachedCountry = localStorage.getItem('eden_cc');
              if (cachedCountry) {
                callback(cachedCountry);
                return;
              }
            } catch (_error) {
              // Local storage can be unavailable in privacy modes.
            }

            fetch('https://ipwho.is/')
              .then((response) => response.json())
              .then((data) => {
                const country = (data?.country_code || 'fr').toLowerCase();
                try {
                  localStorage.setItem('eden_cc', country);
                } catch (_error) {
                  // The form still works if the result cannot be cached.
                }
                callback(country);
              })
              .catch(() => callback('fr'));
          },
          utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/18.2.1/js/utils.js',
        })
      : null;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const honeypot = form.querySelector('[name="company_website"]');
      const successUrl = section.dataset.successUrl || '/pages/thanks';

      if (honeypot?.value) {
        window.location.href = successUrl;
        return;
      }

      if (!form.checkValidity()) {
        setStatus(statusElement, 'Please complete all required fields.', 'error');
        form.reportValidity();
        return;
      }

      const email = emailField.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setStatus(statusElement, 'Please enter a valid email address.', 'error');
        emailField.focus();
        return;
      }

      if (!phoneField.value.trim()) {
        setStatus(statusElement, 'Please enter your phone number.', 'error');
        phoneField.focus();
        return;
      }

      if (phoneInput && phoneInput.isValidNumber && !phoneInput.isValidNumber()) {
        setStatus(statusElement, 'Please enter a valid phone number.', 'error');
        phoneField.focus();
        return;
      }

      const tracking = window.edenTracking ? window.edenTracking.capture() : {};
      const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
      const countryData = phoneInput ? phoneInput.getSelectedCountryData() : null;
      const value = (name) => form.querySelector(`[name="${name}"]`)?.value.trim() || '';
      const newsletter = form.querySelector('[name="newsletter"]');
      const payload = {
        form_name: section.dataset.formName || 'collection-page-collection',
        first_name: value('first_name'),
        last_name: value('last_name'),
        email,
        country_code: getCountryCallingCode(phoneInput),
        country_phone: getCountryName(countryData),
        phone: phoneField.value.trim(),
        budget: value('budget'),
        message: value('message'),
        newsletter: Boolean(newsletter?.checked),
        sku: value('sku'),
        product: value('product'),
        product_url: value('product_url'),
        utm_source: tracking.utm_source || '',
        utm_medium: tracking.utm_medium || '',
        utm_campaign: tracking.utm_campaign || '',
        utm_content: tracking.utm_content || '',
        utm_term: tracking.utm_term || '',
        gclid: tracking.gclid || '',
        fbclid: tracking.fbclid || '',
        referrer: tracking.referrer || '',
        form_url: window.location.href,
        device: isMobile ? 'Mobile' : 'Desktop',
      };

      submitButton.disabled = true;
      setStatus(statusElement, 'Sending…', '');

      try {
        const recaptcha = await getRecaptchaToken(section);
        if (section.dataset.recaptchaRequired === 'true' && !recaptcha.token) {
          throw new Error('reCAPTCHA did not return a token');
        }
        payload.recaptcha_token = recaptcha.token;
        payload['g-recaptcha-response'] = recaptcha.token;
        payload.recaptcha_action = recaptcha.action;

        const response = await fetch(section.dataset.webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (window.edenTracking) window.edenTracking.clear();
        window.location.href = successUrl;
      } catch (_error) {
        setStatus(statusElement, 'Something went wrong. Please try again.', 'error');
        submitButton.disabled = false;
      }
    });
  }

  function initializeSection(section) {
    if (!section.matches('[data-yoel-proposal-root], [data-yoel-artwork-collection]')) return;
    section.querySelectorAll('[data-yoel-proposal-form]').forEach((form) => initializeForm(form, section));
  }

  function initializeAll(root = document) {
    const sectionSelector = '[data-yoel-proposal-root], [data-yoel-artwork-collection]';

    if (root instanceof Element && root.matches(sectionSelector)) {
      initializeSection(root);
    }
    root.querySelectorAll(sectionSelector).forEach(initializeSection);
  }

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
